// The Features sheet: every RAW feature in the project, one row per drawn
// geometry — never expanded by block, flow or level (the Areas sheet, beside
// it, is where that split lives). A feature's own raw properties are typed
// over here directly, exactly like Usages and Blocks, and a header typed over
// a blank column becomes a new raw property the moment it is filled in.
//
// Same column SET as the pre-split Features sheet (main branch) — every raw
// property, `id`/`appId`/`projectId`/`public` hidden, every underscore field
// read-only, no resolved or baked-joined extra columns — but not the same
// column ORDER or sort. Pinned front block, left to right: Feature (column
// 1, the row label) · Show · Usage · Layer Id · Building ID (`buildingID`,
// Giraffe's own raw spelling — an ordinary read-only field like any other —
// ASKED to be pinned, not a special case) · Levels (column F, user-specified
// position), then every other raw property A-Z
// (`featureColumnSort`), not main's pinned-then-grouped-then-alphabetical
// default. The row label itself is also not main's: `featureLabel.ts`'s
// Usage + Block Name + Building ID scheme, with a " (1)"/" (2)" suffix for a
// label two rows share, instead of main's bare "Usage tail4" — Block Name is
// resolved only to help BUILD that label; it is not a column of its own.
// One more difference from main: a bare raw property and its
// underscore-prefixed mirror of the exact same value (`blockId`/`_blockId`,
// `stackOrder`/`_stackOrder` — confirmed live, `rawSections` itself carries
// both, not a gtables merge artifact) collapse to just the bare one, instead
// of both showing as separate, redundant columns.

import type { CellValue, Sheet, Workbook } from '@/model/types';
import { naturalCompare } from '@/model/collate';
import { createSheet, getCell, newId, sheetByRole, uniqueSheetName } from '@/model/workbook';
import { applyStructural, deletionMap } from '@/model/structure';
import type { GiraffeData } from '@/data/types';
import { unitsOf } from '@/data/units';
import { featureLabel, SHOW_FIELD } from './featureLabel';
import { usageOptions } from './usagesTable';

export { SHOW_FIELD };
import {
  FIRST_DATA_ROW,
  HEADER_ROW,
  columnsOf,
  findField,
  orderByLayerTree,
  sortRows,
  squashField,
  tableDiff,
  writeTable,
  type CellProvenance,
  type EntityDiff,
  type EntityRow,
  type TableSpec,
} from './entityTable';

export const FEATURES_SHEET_NAME = 'Features';

/** Feature (column 1, the label) · Show · Usage · Layer Id · Building ID · Levels, then everything else A-Z. */
const COLUMN_ORDER = [SHOW_FIELD, 'usage', 'layerId', 'buildingID', 'levels'];

/**
 * The building-id field, squashed — a raw feature can carry this under any
 * spelling (confirmed live: `buildingID`, no underscore, capital ID — not
 * gTables' own invented camelCase `_buildingId`). Matched so the pinned
 * column above actually catches it, whatever a given project spells it as —
 * see `findField`'s doc comment in entityTable.ts.
 */
const BUILDING_ID_SQUASHED = squashField('buildingID');

/**
 * Fields with no column at all: the handles, not the facts — see
 * areasTable.ts's `HIDDEN` for the full reasoning.
 */
const HIDDEN = new Set(['id', 'appid', 'projectid', 'public']);

/**
 * Fields the project computes or owns, shown but never pushed back — same
 * list as main branch. `buildingID` is listed explicitly, not left to
 * `allUnderscored` below: its real-world spelling (confirmed live) has no
 * leading underscore, so the blanket "every underscore field is read-only"
 * rule would no longer catch it on its own — it is still engine/bookkeeping
 * data, not something this sheet should push back.
 */
const READ_ONLY = new Set(['id', 'area', 'perimeter', 'grossArea', 'netArea', 'saleableArea', 'dwellingTotalCount', 'buildingID']);

/** Every underscore-prefixed raw field — the engine's own bookkeeping, never pushed. */
function allUnderscored(data: GiraffeData): string[] {
  return data.fields('rawSections').filter((f) => f.startsWith('_'));
}

function layerOptions(data: GiraffeData): string[] {
  const out = new Set<string>();
  for (const row of data.rows('rawSections')) {
    const layer = row.properties.layerId;
    if (typeof layer === 'string' && layer.trim() !== '') out.add(layer.trim());
  }
  const treeOrder = data.drawingLayers?.() ?? [];
  for (const layer of treeOrder) out.add(layer);
  // Tree order, not alphabetical (user-requested) — same order the rows
  // themselves sort by (`sortRows`), so the dropdown and the sheet agree.
  return orderByLayerTree(out, treeOrder);
}

interface MergedFeatures {
  rows: EntityRow[];
  rowIds: Map<string, string>;
}

/**
 * The rows: one per raw feature, full stop — no block/flow/level expansion,
 * and no extra columns. Block Name and Building ID are resolved here ONLY to
 * build the row's label (`featureLabel`) — a baked-only building id
 * (confirmed — `rawSections` never carries a baked output on its own) still
 * needs the lightweight join to `bakedSections` by id, the same as before;
 * it just no longer becomes a column.
 */
function mergeFeatureRows(data: GiraffeData, hidden: ReadonlySet<string>): MergedFeatures {
  const buildingIdById = new Map<string, unknown>();
  for (const row of data.rows('bakedSections')) {
    const id = String(row.id);
    if (!buildingIdById.has(id)) buildingIdById.set(id, findField(row.properties, 'buildingID'));
  }
  const blocks = data.blocks();

  // Confirmed live: `rawSections` itself can carry both a bare property and
  // an underscore-prefixed mirror of the SAME value — `blockId`/`_blockId`,
  // `stackOrder`/`_stackOrder` — not a gtables merge artifact (unlike
  // Areas', which joins raw+baked); this project's own raw data already has
  // both keys. The bare name wins; its mirror is left out, same
  // `squashField` comparison areasTable.ts's merge uses for the same
  // reason, so it does not show up as a second, redundant column.
  //
  // Built ONCE, across every raw feature — not per-row. Confirmed live as
  // the actual bug: a per-row set only drops `_stackOrder` on a feature that
  // ALSO carries bare `stackOrder` itself; a project where some features
  // have only the bare name and others have only the underscore mirror
  // (never both on the same feature) kept both spellings alive across the
  // sheet — `columnsOf`'s union of every row's fields still saw both
  // `stackOrder` and `_stackOrder`, and both prettify to the same "Stack
  // Order" header, so the column showed twice.
  const bareSquashed = new Set<string>();
  for (const row of data.rows('rawSections')) {
    for (const k of Object.keys(row.properties)) if (!k.startsWith('_')) bareSquashed.add(squashField(k));
  }

  interface PreRow {
    id: string;
    base: string;
    fields: Record<string, unknown>;
    provenance: Record<string, CellProvenance>;
  }
  const pre: PreRow[] = [];

  for (const raw of data.rows('rawSections')) {
    const id = String(raw.id);
    const fields: Record<string, unknown> = { [SHOW_FIELD]: !hidden.has(id) };
    for (const [k, v] of Object.entries(raw.properties)) {
      if (k.startsWith('_') && bareSquashed.has(squashField(k))) continue;
      // Whatever this project spells its building id as, it lands under the
      // canonical `buildingID` key so the pinned column above actually
      // matches it, instead of sorting as an ordinary raw-spelled column.
      const outKey = squashField(k) === BUILDING_ID_SQUASHED ? 'buildingID' : k;
      fields[outKey] = v;
    }
    const provenance: Record<string, CellProvenance> = {};

    const blockId = raw.properties.blockId;
    const blockName = typeof blockId === 'string' ? blocks[blockId]?.name : undefined;
    const buildingId = fields.buildingID ?? buildingIdById.get(id);

    const base = featureLabel(raw.properties.usage, buildingId, blockName);
    pre.push({ id, base, fields, provenance });
  }

  // No " (1)"/" (2)" disambiguator — user-requested removal. Two rows CAN
  // now show the identical name; `key` (the feature's own raw id) is what
  // actually tells them apart for matching/push/"Select on map" — see
  // `EntityRow.key`'s own doc comment. `rowIds` stays name-keyed, collapsing
  // duplicates to whichever came last — a fallback for a sheet that has not
  // been written since this shipped and so has no `entityKeys` yet.
  const rowIds = new Map<string, string>();
  const rows: EntityRow[] = pre.map((p) => {
    rowIds.set(p.base.toLowerCase(), p.id);
    return { name: p.base, key: p.id, fields: p.fields, provenance: p.provenance };
  });

  // By layer (tree order), then building, then usage, then name — same
  // order as Areas, see entityTable.ts's `sortRows`. Features rows have no
  // `stackSection`/`levelNumber` of their own, so that key ties for every
  // row and falls straight through to usage/name. Takes PHYSICAL effect on
  // every write too, not just this in-memory order — `spec.forceSort`
  // (user-requested), see its own doc comment.
  sortRows(rows, data.drawingLayers?.() ?? []);
  return { rows, rowIds };
}

/** The one deliberate difference from main's column order: fully alphabetical past the pinned front block. */
function featureColumnSort(fields: string[]): string[] {
  const remaining = new Set(fields);
  const pinned = COLUMN_ORDER.filter((f) => remaining.delete(f));
  const rest = [...remaining].sort((a, b) => naturalCompare(a, b));
  return [...pinned, ...rest];
}

function spec(data: GiraffeData): TableSpec {
  const layers = layerOptions(data);
  const usages = usageOptions(data);
  return {
    label: 'Feature',
    labelFields: new Set(['name', 'title']),
    order: COLUMN_ORDER,
    hidden: HIDDEN,
    lists: {
      ...(layers.length > 0 ? { layerId: { options: layers } } : {}),
      ...(usages ? { usage: usages } : {}),
    },
    readOnly: new Set([...READ_ONLY, SHOW_FIELD, ...allUnderscored(data)]),
    columnSort: featureColumnSort,
    units: unitsOf(data),
    newFields: true,
    // User-requested: match Areas' guaranteed layer/building/usage/name
    // sort on every write, instead of leaving a row wherever it already
    // sits — see `TableSpec.forceSort`'s own doc comment.
    forceSort: true,
  };
}

export function featureRows(data: GiraffeData, hidden: ReadonlySet<string>): EntityRow[] {
  return mergeFeatureRows(data, hidden).rows;
}

export function featureIds(data: GiraffeData, hidden: ReadonlySet<string> = new Set()): Map<string, string> {
  return mergeFeatureRows(data, hidden).rowIds;
}

export function featureColumns(data: GiraffeData, hidden: ReadonlySet<string>): string[] {
  return columnsOf(featureRows(data, hidden), spec(data));
}

export function ensureFeaturesSheet(wb: Workbook): Sheet {
  let sheet = sheetByRole(wb, 'features');
  if (!sheet) {
    sheet = createSheet(uniqueSheetName(wb, FEATURES_SHEET_NAME), newId('features'));
    sheet.role = 'features';
    sheet.colWidths[1] = 190;
    sheet.colWidths[2] = 74;
    wb.sheets.push(sheet);
  }
  /*
   * Show is a two-value column, so it arrives as a dropdown.
   *
   * The chip is what makes the sheet readable at a glance — a column of green
   * TRUEs with three grey FALSEs in it says what is hidden without reading a
   * word — and it is also the only affordance saying the cell has two
   * settings rather than being free text someone has to spell right.
   *
   * Reasserted on EVERY call, not just when the sheet is first created
   * (confirmed live — a sheet that has lived through a lot of structural
   * churn this session lost it somewhere along the way, with nothing ever
   * putting it back, since the old code only ever set it once). Column 2 is
   * always where it is: `SHOW_FIELD` is first in `COLUMN_ORDER` and column 1
   * is always the name, so this does not need to look anything up.
   */
  sheet.dropdowns = { ...(sheet.dropdowns ?? {}), 2: { options: ['TRUE', 'FALSE'], colours: ['#6fa86a', '#57606a'] } };
  return sheet;
}

/** See areasTable.ts's `dropHiddenAreaColumns` — same reasoning, this sheet's own hidden-field set. */
export function dropHiddenColumns(wb: Workbook, sheet: Sheet): void {
  const doomed: number[] = [];
  for (let c = 1; c <= sheet.cols; c++) {
    const v = getCell(sheet, HEADER_ROW, c)?.v;
    if (v === undefined || v === null) continue;
    if (HIDDEN.has(squashField(String(v)))) doomed.push(c);
  }
  if (doomed.length === 0) return;
  applyStructural(wb, sheet, 'col', deletionMap(doomed));
}

export function writeFeatures(
  sheet: Sheet,
  data: GiraffeData,
  hidden: ReadonlySet<string>,
  preserveEdits = true,
  wb?: Workbook
): void {
  writeTable(sheet, featureRows(data, hidden), spec(data), preserveEdits, wb);
}

export function featureDiff(sheet: Sheet, data: GiraffeData, hidden: ReadonlySet<string>): EntityDiff {
  return tableDiff(sheet, featureRows(data, hidden), spec(data));
}

/**
 * Which features the sheet says are switched off — see areasTable.ts's
 * `hiddenFromAreasSheet`, same mechanism, this sheet's own row set.
 */
export function hiddenFromSheet(sheet: Sheet, data: GiraffeData, hidden: ReadonlySet<string>): Set<string> {
  const columns = featureColumns(data, hidden);
  const showCol = columns.indexOf(SHOW_FIELD);
  const out = new Set<string>();
  if (showCol < 0) return out;
  const ids = featureIds(data, hidden);
  const last = FIRST_DATA_ROW + featureRows(data, hidden).length + 40;
  for (let r = FIRST_DATA_ROW; r <= last; r++) {
    // `entityKeys` (by physical row) first — two rows can now legitimately
    // show the identical name (the " (1)"/" (2)" disambiguator was
    // removed), so the name-text lookup below can no longer tell them
    // apart on its own; kept only as a fallback for a sheet not yet
    // rewritten since `EntityRow.key` shipped.
    let id = sheet.entityKeys?.[r];
    if (id === undefined) {
      const label = getCell(sheet, r, 1)?.v;
      if (label === undefined || label === null) continue;
      id = ids.get(String(label).trim().toLowerCase());
    }
    if (id === undefined) continue;
    if (isOff(getCell(sheet, r, 2 + showCol)?.v)) out.add(id);
  }
  return out;
}

/** FALSE, "no", 0 and an unticked box all mean off. Anything else is on. */
function isOff(value: CellValue | undefined): boolean {
  if (value === false || value === 0) return true;
  if (typeof value !== 'string') return false;
  const t = value.trim().toLowerCase();
  return t === 'false' || t === 'no' || t === 'off' || t === 'hide' || t === '0';
}
